/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import clone from 'clone';
import {
  Button,
  Container,
  Dimmer,
  Grid,
  Loader,
  Segment,
  Header,
  Label,
} from 'semantic-ui-react';
import { Link } from 'react-router';

import LoanTracker from '../LoanTracker/LoanTracker';
import BookingSteppers from '../../components/BookingSteppers';
import config from '../../../config';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import { promptChangesLeave, promptChangesMessage } from '../../actions/prompt';

const isPRS = config.get('prs');

class Loan extends Component {
  static contextTypes = {
    store: PropTypes.object,
    router: PropTypes.object,
  }
  static propTypes = {
    auth: PropTypes.shape({
      isHeadquarters: PropTypes.bool,
      isSuperAdmin: PropTypes.bool,
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
    route: PropTypes.shape({}),
    token: PropTypes.string,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.object,
      search: PropTypes.string,
    }).isRequired,
  }
  static defaultData = {
    bankCases: [],
  };
  static mapStateToProps = state => ({
    auth: state.auth,
  })
  static getPropsToState = (props) => {
    const loading = props.loading;
    const data = Object.assign({}, Loan.defaultData, props.data);
    if (!data.bankCases) {
      data.bankCases = [];
    }
    return {
      loading,
      data,
      oriData: clone(data),
    };
  }
  constructor(props) {
    super(props);
    const state = Loan.getPropsToState(props);
    this.state = {
      ...state,
      allBankCases: [],
      systemData: [],
      hasDropCase: false,
      isMortgageRequired: false,
    };
  }
  componentDidMount() {
    if (!isPRS) {
      this.fetchSystemSettings();
    }
  }
  componentWillReceiveProps(nextProps) {
    const state = Loan.getPropsToState(nextProps);
    // don't allow state to override data if oriData is same
    if (JSON.stringify(this.state.oriData) === JSON.stringify(state.oriData)) {
      state.data = this.state.data;
    }
    if (JSON.stringify(this.state.data) !== JSON.stringify(state.data)) {
      const data = state.data;
      if (data) {
        if (isPRS) {
          this.handleFetchAllBankCases(data);
        } else {
          this.checkIsMortgageRequired(this.state.systemData, data);
        }
      }
    }
    this.setState({ ...state });
  }
  setLoading = (loading = false) => (new Promise((res) => {
    this.setState({ loading }, res);
  }))
  getAllBankCases = (bankCases, bankCaseDrops) => {
    let hasDropCase = false;
    const safeBankCases = Array.isArray(bankCases) ? bankCases : [];
    const safeBankCaseDrops = Array.isArray(bankCaseDrops) ? bankCaseDrops : [];
    const bankerIDs = new Set(safeBankCases.map(bc => bc.bankerID));
    const allCases = safeBankCaseDrops.length > 0 ? [...safeBankCases, ...safeBankCaseDrops.filter((bcd) => {
      if (!bankerIDs.has(bcd.bankerID)) {
        hasDropCase = true;
        return true;
      }
      return false;
    })] : [...safeBankCases];

    this.setState({ allBankCases: allCases, hasDropCase }, () => this.checkBeforeLeave());
  }
  checkBeforeLeave() {
    const hasBankCases = this.state.allBankCases && this.state.allBankCases.length > 0;

    this.context.router.setRouteLeaveHook(this.props.route, (nextLocation) => {
      if (this.state.isMortgageRequired && !hasBankCases && this.state.data && (this.state.data.paymentMethodID === 'own_banker' || this.state.data.paymentMethodID === 'referred_banker')) {
        const { store } = this.context;
        const callback = () => {
          this.setState({ isMortgageRequired: false }, () => {
            store.dispatch(promptChangesLeave(nextLocation));
          });
        };
        const header = 'Add Banker';
        const message = 'Mortgage step is compulsory, your booking will be auto released. Do you still want to leave?';
        store.dispatch(promptChangesMessage({ header, callback, message }));
        return false;
      }
      return true;
    });
  }
  checkIsMortgageRequired = (systemData, data) => {
    let isMRequired = false;
    if (systemData && data) {
      const project = data.project;

      const systemARenabled = systemData && systemData.arBookingExpIsEnabled;
      const systemTimerSet = systemData && systemData.arBookingExpMoreThanMinutes !== '' && systemData.arBookingExpMoreThanMinutes !== '0';
      const systemMortgageRequired = systemData.arBookingExpMortgageEnabled;

      const projectARenabled = project && project.arBookingExpIsEnabled;
      const projectMortgageRequired = project && project.arBookingExpMoreThanMinutes !== '' && project.arBookingExpMoreThanMinutes !== '0' && project.arBookingExpMortgageEnabled;

      if (systemARenabled) {
        if (systemTimerSet && systemMortgageRequired) {
          isMRequired = true;
        } else if (!systemTimerSet && projectARenabled && projectMortgageRequired) {
          isMRequired = true;
        } else {
          isMRequired = false;
        }
      } else {
        isMRequired = false;
      }

      this.setState({ isMortgageRequired: isMRequired }, () => this.handleFetchAllBankCases(data));
    }
  }
  checkIsNotAuthorizedToInviteBanker = () => {
    const { auth } = this.props;
    const { data } = this.state;
    // Not allowed to invite and role is not HQ nor Super Admin
    if (data && data.project && !data.project.allowAgentSalesTeamBankerInvitation &&
      auth && !auth.isHeadquarters && !auth.isSuperAdmin) {
      return true;
    }
    return false;
  }
  async fetchSystemSettings() {
    await this.setState({ loading: true });
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM('/settings/', {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      await this.setState({ loading: false });
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const systemData = await res.json();
      await this.setState({
        systemData,
      }, () => this.checkIsMortgageRequired(systemData, this.state.data));
    }
    await this.setState({ loading: false });
  }
  handleFetchAllBankCases = (data) => {
    if (data.loanTrackerLeadID > 0 &&
      data.project &&
      data.project.autoAssignBankerStatus !== '' &&
      data.project.autoAssignBankerStatus === 'booked' &&
      data.statusID === 'reserved') {
      this.fetchBankCaseDrops(data);
    } else {
      this.getAllBankCases(data.bankCases);
    }
  }
  fetchBankCaseDrops = async (booking) => {
    await this.setLoading(true);
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`/bookings/${booking.id}/loan_trackers/bank_cases/drop/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.token,
        },
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: 'There was an error with your request. Please try again.' }));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const data = await res.json();
      if (data && data.length > 0) {
        this.getAllBankCases(booking.bankCases, data);
      } else {
        this.getAllBankCases(booking.bankCases);
      }
    }
    await this.setLoading(false);
  };
  refreshBankingData = async () => {
    const { data } = this.state;
    if (data && data.id) {
      try {
        await this.setLoading(true);
        const { store } = this.context;
        const res = await store.dispatch(fetchM(`/bookings/${data.id}/`, {
          credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Csrf-Token': this.props.token,
          },
        }));
        if (res.ok) {
          const updatedData = await res.json();
          this.setState({
            data: updatedData,
            oriData: clone(updatedData),
          }, () => {
            this.handleFetchAllBankCases(updatedData);
          });
        }
      } finally {
        await this.setLoading(false);
      }
    }
  };
  async generateBankerBookingForm(bookingId) {
    await this.setLoading(true);
    const { store } = this.context;
    try {
      await store.dispatch(fetchM(`/bookings/${bookingId}/print_banker/`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.token,
        },
      }));
      store.dispatch(flashSuccessMessage({ message: 'Booking form has been shared with the banker(s).' }));
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
      } else {
        store.dispatch(flashErrorMessage({ message: 'An error occurred while sharing the form.' }));
      }
    } finally {
      await this.setLoading(false);
    }
  }
  handleBankerAssignmentSuccess = async () => {
    const { data } = this.state;
    await this.refreshBankingData();
    if (data && data.project && data.project.autoShareFormToBanker) {
      this.generateBankerBookingForm(data.id);
    }
  }
  render() {
    const { allBankCases, data, hasDropCase, isMortgageRequired } = this.state;
    const projectUnit = data.projectUnit;
    const project = data.project;
    return (
      <Container>
        <Grid>
          <Dimmer style={{ zIndex: 20 }} active={this.state.loading} inverted>
            <Loader inline="centered">Loading</Loader>
          </Dimmer>
          <Grid.Row>
            <Grid.Column width={16} textAlign="center">
              <Header as="h4" textAlign="center" style={{ marginTop: '1em' }}>
                Booking for <br className="large-hide small-hide tablet-hide" /><Link to={`/projects/${project && project.id}/units/${projectUnit && projectUnit.id}/`}>{projectUnit && projectUnit.reference}</Link>, {project && project.name} {projectUnit && projectUnit.isBumi && <Label size="mini">Bumi</Label>}
              </Header>
              {
                project && (project.paymentFlow || project.loanTrackerDirectShow) ?
                  <BookingSteppers
                    step={project.paymentFlow ? 3 : 2}
                    totalSteps={project.paymentFlow && project.loanTrackerDirectShow ? 3 : 2}
                    lastStepLabel={project.paymentFlow && !project.loanTrackerDirectShow ? 'Payment' : 'Mortgage'}
                  />
                  : ''
              }
              <Header className="new_title" as="h4" attached="top">
                LOAN TRACKER
              </Header>
              <Segment style={{ marginBottom: '1em' }} attached>
                <p>Help purchasers kickstart the loan application process in 3 easy steps.</p>
                <LoanTracker
                  auth={this.props.auth}
                  allBankCases={allBankCases}
                  bookingID={data.id}
                  bookingStatusID={data.statusID}
                  bookingSPAValue={data.spaValue}
                  bankerSelectionTabs={data.project && data.project.bankerSelectionTabs ? data.project.bankerSelectionTabs : []}
                  hasDropCase={hasDropCase}
                  isCashBuyer={data.paymentMethodID === 'cash'}
                  isNotAuthorizedToInviteBanker={this.checkIsNotAuthorizedToInviteBanker()}
                  loanTrackerLeadID={data.loanTrackerLeadID}
                  loanTrackerLeadDropID={data.loanTrackerLeadDropID}
                  loanTrackerLeadCode={data.loanTrackerLeadCode}
                  loanTrackerLeadConsentPinExpiresAt={data.loanTrackerLeadConsentPinExpiresAt}
                  loanTrackerLeadConsentPinCount={data.loanTrackerLeadConsentPinCount}
                  token={this.props.token}
                  consentTypeID={data.project ? data.project.loanTrackerProjectConsentTypeID : null}
                  leadBankersLimit={data.project ? data.project.loanTrackerProjectLeadBankersLimit : 0}
                  preferredName={data.bookingCustomer ?
                    data.bookingCustomer.preferredName : null}
                  mobilePrefix={data.bookingCustomer ?
                    data.bookingCustomer.mobilePrefix : null}
                  mobile={data.bookingCustomer ?
                    data.bookingCustomer.mobile : null}
                  projectID={data.projectID}
                  projectAssignBankCasesStatus={data.project ? data.project.autoAssignBankerStatus : ''}
                  projectCurrencies={data.project ? data.project.projectCurrency : ''}
                  bankCases={data.bankCases}
                  leadStatus={data.leadStatus}
                  leadCreatedAt={data.leadCreatedAt}
                  bankerOnCheck={this.checkBanker}
                  setLoading={this.setLoading}
                  location={this.props.location}
                  onBankerAssignmentSuccess={this.handleBankerAssignmentSuccess}
                  project={data.project}
                />
              </Segment>
              {
                !isPRS ? (
                  <Button
                    as={Link}
                    color="green"
                    disabled={
                    isMortgageRequired &&
                    allBankCases.length < 1 &&
                      (data.paymentMethodID === 'own_banker' || data.paymentMethodID === 'referred_banker')
                  }
                    to={`/bookings/${data.id}/`}
                  >
                  Complete the booking
                </Button>
              ) : (
                <p><Link to={`/bookings/${data.id}/`}>Skip to booking</Link></p>
                )
              }
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    );
  }
}

export default connect(Loan.mapStateToProps)(Loan);



// WEBPACK FOOTER //
// ./src/containers/Bookings/Loan.jsx